import React from 'react';
import { mapKeys, camelCase, find } from 'lodash';
import { arrayOf, shape, string, number, object } from 'prop-types';
import { logger, stats } from '@fiverr-private/obs';
import SessionGlobalMessages from '@fiverr-private/session_global_messages';
import { CURRENCY_CODES } from '@fiverr-private/localization';
import cookie from '../../lib/cookie';
import { DISCOVER_FIVERR_TRANSLATION_PREFIX } from './DiscoverFiverr/constants';
import { STORED_MESSAGES_TYPES } from './constants';
import DiscoverFiverr, { show as showDiscoverFiverr } from './DiscoverFiverr';
import UserActivation, { show as showUserActivation } from './UserActivation';
import TaxFormNotice from './TaxFormNotice';
import IdentityVerification from './IdentityVerification';
import RealtimeMessages, { serializer as realtimeMessagesSerializer } from './RealtimeMessages';
import ReferralProgramInviteeDiscountNotice from './ReferralProgramInviteeDiscountNotice';
import CustomPromoCodeDiscountNotice from './CustomPromoCodeDiscountNotice';
import RangesPromoCodeDiscountNotice from './RangesPromoCodeDiscountNotice';

/**
 * Translates a given text and locale with fiverr discover prefix
 * @param {String} text The scoped text key to translate.
 * @param {String} locale The locale to use.
 * @return {String}
 */
const translateDiscoverFiverrText = (text, locale) => i18n.t(
    `${DISCOVER_FIVERR_TRANSLATION_PREFIX}.${text}`,
    { $scope: locale }
);

/**
 * Serializes the layout data into a format accepted by this component.
 * @param {Context} options.context The current request's context.
 * @param {String[]} options.locales The current request's user data.
 * @param {User} options.user The current request's user data.
 * @param {StoredGlobalMessage[]} options.storedGlobalMessages A list of current user stored global messages.
 * @return {Object}
 */
export const serializer = ({
    context,
    locales,
    user = {},
    storedGlobalMessages = [],
    currencies
}) => {
    const usdCurrency = find(currencies, (currency) => currency.code === CURRENCY_CODES.USD);

    return ({
        context,
        locales,
        user,
        discoverFiverr: {
            intro: translateDiscoverFiverrText('intro', context.browserLanguage),
            letsGo: translateDiscoverFiverrText('lets_go', context.browserLanguage)
        },
        storedMessages: storedGlobalMessages.map(({ id, content_type, ...rest }) => ({
            id,
            type: content_type,
            showPercentageDiscount: !usdCurrency || usdCurrency.symbol !== rest.discount_symbol,
            ...mapKeys(rest, (_, key) => camelCase(key))
        }))
    });
};

/**
 * @see {@link https://github.com/fiverr/pinned_flashes_chimera/blob/master/lib/views/global_messages_view.rb#L12-L18|Stored global messages prioritization}
 */
export const StoredGlobalMessages = ({ storedMessages = [], user }) => (
    storedMessages.map(({ type, id, ...rest }) => {
        const msgProps = {
            key: id,
            id,
            ...rest
        };

        stats.count('stored_global_messages', type);

        switch (type) {
            case STORED_MESSAGES_TYPES.TAX_FORM_NOTICE:
                return <TaxFormNotice {...msgProps} username={user.username}/>;
            case STORED_MESSAGES_TYPES.IDENTITY_VERIFICATION:
                return <IdentityVerification deadline={msgProps.verificationDeadline}/>;
            case STORED_MESSAGES_TYPES.REFERRAL_PROGRAM_INVITEE_DISCOUNT:
                return (
                    <ReferralProgramInviteeDiscountNotice messageId={msgProps.messageIdentifier}
                        userId={user.id}/>
                );
            case STORED_MESSAGES_TYPES.CUSTOM_DISCOUNT_PROMO_CODE:
                return (
                    <CustomPromoCodeDiscountNotice messageId={msgProps.messageIdentifier}
                        userId={user.id}
                        withExpirationDate={msgProps.showExpirationDate}
                        discountValue={msgProps.discountValue}
                        code={msgProps.code}
                        expirationDate={msgProps.expirationDate}
                        termsAndConditionSlug={msgProps.termsAndConditionsSlug}/>
                );
            case STORED_MESSAGES_TYPES.DISCOUNT_RANGES_PROMO_CODE:
                return (
                    <RangesPromoCodeDiscountNotice messageId={msgProps.messageIdentifier}
                        userId={user.id}
                        withExpirationDate={msgProps.showExpirationDate}
                        discountValue={msgProps.discountValue}
                        orderValue={msgProps.orderValue}
                        code={msgProps.code}
                        showPercentageDiscount={msgProps.showPercentageDiscount}
                        expirationDate={msgProps.expirationDate}
                        termsAndConditionSlug={msgProps.termsAndConditionsSlug}/>
                );
            default:
                logger.warn(`Unfamiliar global message type: ${type} for user: ${user.id}.`);
                return null;
        }
    })
);

const ContextualGlobalMessages = ({ context, locales, user, discoverFiverr, cookies }) => (
    <>
        {showDiscoverFiverr({
            user,
            context,
            locales,
            cookies
        }) && <DiscoverFiverr browserLanguage={context.browserLanguage}
            url={context.url}
            introText={discoverFiverr.intro}
            letsGoText={discoverFiverr.letsGo}/>
        }
        {showUserActivation({
            user,
            context
        }) && <UserActivation user={user}/>}
    </>
);

const GlobalMessages = ({ context, locales, storedMessages = [], user, discoverFiverr, headerCookies }) =>
    (
        <>
            <SessionGlobalMessages messages={context.messages}/>
            <StoredGlobalMessages storedMessages={storedMessages} user={user}/>
            <ContextualGlobalMessages user={user}
                cookies={cookie.getAll(headerCookies)}
                context={context}
                discoverFiverr={discoverFiverr}
                locales={locales}/>
            { !!user.id && <RealtimeMessages {...realtimeMessagesSerializer({ user })}/> }
        </>
    );

StoredGlobalMessages.propTypes = {
    storedMessages: arrayOf(shape({
        id: string,
        type: string
    })),
    userId: number
};

ContextualGlobalMessages.propTypes = {
    user: object,
    cookies: object,
    context: object,
    discoverFiverr: shape({
        intro: string.isRequired,
        letsGo: string.isRequired
    }),
    locales: arrayOf(object)
};

GlobalMessages.propTypes = {
    ...ContextualGlobalMessages.propTypes,
    ...StoredGlobalMessages.propTypes,
    headerCookies: string
};

export default GlobalMessages;
